import {useMemo} from 'react';

// Custom hook for pagination
export type UsePaginationArgs = {
  totalPages: number;
  currentPage: number;
  siblingCount?: number;
};

// Helper function to calculate the range of page number options we want to display
function getPageRange(start: number, end: number): number[] {
  const range: number[] = [];
  for (let i = start; i <= end; i++) {
    range.push(i);
  }
  return range;
}

export default function usePagination({
  totalPages,
  currentPage,
  siblingCount = 1,
}: UsePaginationArgs): (number | string)[] {
  const paginationRange = useMemo(() => {
    // The number of pages options we want to display on the UI: sibling count*2 + first page + last page + dots*2 + current page
    const totalPagesNumber = siblingCount * 2 + 5;

    // Scenario 1: If the number of pages available is less than the number of page options we want to show
    if (totalPagesNumber > totalPages) {
      return getPageRange(1, totalPages);
    }

    // Calculate the indexes of the extreme siblings
    const leftMostSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightMostSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    // Only show the dots if there's more than 1 items between the extreme siblings and the first/last page.
    const showLeftDots = leftMostSiblingIndex > 2;
    const showRightDots = rightMostSiblingIndex < totalPages - 2;

    // Scenario 2: No dots on the left, but there're dots on the right
    if (!showLeftDots && showRightDots) {
      // const leftItemCount = totalPagesNumber - 2;
      const leftRange = getPageRange(1, totalPagesNumber - 2);
      return [...leftRange, 'dots', totalPages];
    }

    // Scenario 3: No dots on the right, but there're dots on the left
    if (showLeftDots && !showRightDots) {
      // const rightItemCount = totalPagesNumber - 2;
      const rightRange = getPageRange(totalPages - totalPagesNumber + 3, totalPages);
      return [1, 'dots', ...rightRange];
    }

    // Scenario 4: Dots on both sides
    const middleRange = getPageRange(leftMostSiblingIndex, rightMostSiblingIndex);
    return [1, 'dots', ...middleRange, 'dots', totalPages];
  }, [totalPages, currentPage, siblingCount]);

  return paginationRange;
}
